import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/apiClient";
import {
  createStueckliste,
  updateStueckliste,
  type Stueckliste,
  type StuecklisteUpsertPayload,
} from "@/lib/stuecklisten";

export type StuecklisteSaveState = "idle" | "pending" | "saving" | "saved" | "error";

interface UseStuecklisteAutoSaveOptions {
  orderId: number;
  editingId: number | null;
  creating: boolean;
  dirty: boolean;
  /** Ändert sich bei jedem Formular-Edit – setzt Debounce zurück. */
  formKey: string;
  editable: boolean;
  debounceMs?: number;
  buildPayload: () => StuecklisteUpsertPayload;
  /** false = Speichern überspringen (z. B. unvollständige Beschichtung), Dirty bleibt. */
  canSave?: () => boolean;
  onSaved: (
    saved: Stueckliste,
    meta: { created: boolean; payload: StuecklisteUpsertPayload },
  ) => void;
}

function parseSavedAt(value?: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function useStuecklisteAutoSave({
  orderId,
  editingId,
  creating,
  dirty,
  formKey,
  editable,
  debounceMs = 700,
  buildPayload,
  canSave,
  onSaved,
}: UseStuecklisteAutoSaveOptions) {
  const [saveState, setSaveState] = useState<StuecklisteSaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const savingRef = useRef(false);
  const queuedRef = useRef(false);
  const editingIdRef = useRef(editingId);
  const creatingRef = useRef(creating);
  const dirtyRef = useRef(dirty);
  const buildPayloadRef = useRef(buildPayload);
  const canSaveRef = useRef(canSave);
  const onSavedRef = useRef(onSaved);

  editingIdRef.current = editingId;
  creatingRef.current = creating;
  dirtyRef.current = dirty;
  buildPayloadRef.current = buildPayload;
  canSaveRef.current = canSave;
  onSavedRef.current = onSaved;

  useEffect(() => {
    setSaveError(null);
    setSaveState("idle");
  }, [editingId, creating, orderId]);

  const performSave = useCallback(async () => {
    if (!editable || savingRef.current) {
      if (dirtyRef.current) queuedRef.current = true;
      return;
    }
    if (canSaveRef.current && !canSaveRef.current()) {
      return;
    }
    const currentEditingId = editingIdRef.current;
    const isCreating = creatingRef.current && currentEditingId == null;
    if (!isCreating && currentEditingId == null) return;

    savingRef.current = true;
    setSaveState("saving");
    setSaveError(null);
    try {
      const payload = buildPayloadRef.current();
      let saved: Stueckliste;
      let created = false;
      if (isCreating) {
        saved = await createStueckliste(orderId, payload);
        created = true;
        // Nach Create muss editingId für Folge-Updates stehen (Parent setzt State async).
        editingIdRef.current = saved.id;
        creatingRef.current = false;
      } else {
        saved = await updateStueckliste(currentEditingId!, payload);
      }
      onSavedRef.current(saved, { created, payload });
      setLastSavedAt(parseSavedAt(saved.updated_at) ?? new Date());
      setSaveError(null);
      setSaveState("saved");
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Automatisches Speichern fehlgeschlagen. Bitte Internetverbindung prüfen.";
      setSaveError(message);
      setSaveState("error");
    } finally {
      savingRef.current = false;
      if (queuedRef.current && dirtyRef.current) {
        queuedRef.current = false;
        void performSave();
      }
    }
  }, [editable, orderId]);

  const flushSave = useCallback(async () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await performSave();
  }, [performSave]);

  const retrySave = useCallback(async () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await performSave();
  }, [performSave]);

  useEffect(() => {
    if (!editable || !dirty) return;
    if (!creating && editingId == null) return;
    setSaveState("pending");
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void performSave();
    }, debounceMs);
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [creating, editingId, dirty, formKey, editable, debounceMs, performSave]);

  return { saveState, flushSave, retrySave, lastSavedAt, saveError };
}
